import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { server } from "../server";
import { toast } from "react-toastify";
import { IconButton } from "@mui/material";
import { BsArrowLeft } from "react-icons/bs";
import { FiTrash2 } from "react-icons/fi";
import ProgressUpdateModal from "../Components/ProgressUpdateModal";
import AddRevenueModal from "../Components/AddRevenueModal";
import useAuth from "../useAuth";


export default function SchemeDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const { schemeId } = location.state || {};
  const [scheme, setScheme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [isProgressModalOpen, setIsProgressModalOpen] = useState(false);
  const [progressUpdates, setProgressUpdates] = useState([]);
  const [updatesLoading, setUpdatesLoading] = useState(false);
  const [isRevenueModalOpen, setIsRevenueModalOpen] = useState(false);
  const [revenues, setRevenues] = useState([]);
  const [revenueLoading, setRevenueLoading] = useState(false);
  const { user } = useAuth();



  useEffect(() => {
    if (schemeId) {
      fetchSchemeDetails();
      fetchProgressUpdates();
      fetchRevenues(schemeId);
    }
  }, [schemeId]);

  const fetchRevenues = async (targetId) => {
    const activeSchemeId = targetId || schemeId || scheme?.id;
    if (!activeSchemeId) return;

    try {
      setRevenueLoading(true);
      const { data } = await axios.get(`${server}/scheme/revenue/all`, {
        params: {
          pageNo: 1,
          pageSize: 100,
          schemeId: activeSchemeId,
          deleted: false,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setRevenues(data.data || []);
    } catch (error) {
      console.error("Error fetching revenues:", error);
    } finally {
      setRevenueLoading(false);
    }
  };

  const handleDeleteRevenue = async (revenueId) => {
    if (!window.confirm("Are you sure you want to delete this revenue entry?")) {
      return;
    }
    if (user.userType !== "admin") {
      toast.error("You are not authorized to delete this revenue entry");
      return;
    }
    try {
      await axios.delete(`${server}/scheme/revenue/deleteById`, {
        params: { revenueId: revenueId },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      toast.success("Revenue entry deleted successfully");
      fetchRevenues();
      fetchSchemeDetails();
    } catch (error) {
      console.error("Error deleting revenue:", error);
      toast.error(
        error.response?.data?.message || "Failed to delete revenue entry"
      );
    }
  };

  const fetchProgressUpdates = async () => {
    try {
      setUpdatesLoading(true);
      const { data } = await axios.get(`${server}/scheme/progressUpdate/all`, {
        params: {
          pageNo: 1,
          pageSize: 100,
          schemeId: schemeId,
          deleted: false,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setProgressUpdates(data.data || []);
    } catch (error) {
      console.error("Error fetching progress updates:", error);
    } finally {
      setUpdatesLoading(false);
    }
  };


  const fetchSchemeDetails = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${server}/scheme/getById`, {
        params: { schemeId: schemeId },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setScheme(data.data);
      if (data.data?.id) {
        fetchRevenues(data.data.id);
      }
    } catch (error) {
      console.error("Error fetching scheme details:", error);
      toast.error("Failed to fetch scheme details");
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "Not set";
    return new Date(timestamp).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      Completed: "bg-green-600 text-white",
      Ongoing: "bg-blue-600 text-white",
      Planning: "bg-yellow-700 text-white",
      Approved: "bg-blue-600 text-white",
      Started: "bg-blue-400 text-white",
      "On Hold": "bg-orange-700 text-white",
      Cancelled: "bg-red-700 text-white",
      "Partially Completed": "bg-teal-700 text-white",
    };
    return colors[status] || "bg-gray-700 text-white";
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      Completed: "bg-green-600 text-white",
      Partial: "bg-yellow-700 text-white",
      Pending: "bg-orange-700 text-white",
      Hold: "bg-red-700 text-white",
      Rejected: "bg-gray-700 text-white",
    };
    return colors[status] || "bg-gray-500 text-white";
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 100) return "bg-green-500";
    if (percentage >= 75) return "bg-blue-500";
    if (percentage >= 50) return "bg-yellow-500";
    if (percentage >= 25) return "bg-orange-500";
    return "bg-red-500";
  };

  // Tab configuration with icons and colors
  const tabs = [
    {
      id: "overview",
      label: "Overview",
      icon: "📊",
      gradient: "from-blue-500 to-blue-600",
      hoverGradient: "from-blue-600 to-blue-700",
      description: "View scheme details",
    },
    {
      id: "financial",
      label: "Financial",
      icon: "💰",
      gradient: "from-green-500 to-green-600",
      hoverGradient: "from-green-600 to-green-700",
      description: "Manage budgets & expenses",
    },
    {
      id: "revenue",
      label: "Revenue",
      icon: "💵",
      gradient: "from-emerald-500 to-teal-600",
      hoverGradient: "from-emerald-600 to-teal-700",
      description: "Track revenue collections",
    },
    // {
    //   id: "approvals",
    //   label: "Approvals",
    //   icon: "✅",
    //   gradient: "from-purple-500 to-purple-600",
    //   hoverGradient: "from-purple-600 to-purple-700",
    //   description: "Track approval status",
    // },
    {
      id: "execution",
      label: "Execution",
      icon: "⚡",
      gradient: "from-orange-500 to-orange-600",
      hoverGradient: "from-orange-600 to-orange-700",
      description: "Monitor progress",
    },
    {
      id: "documents",
      label: "Documents",
      icon: "📑",
      gradient: "from-gray-600 to-gray-700",
      hoverGradient: "from-gray-700 to-gray-800",
      description: "Access files & reports",
    },
    {
      id: "history",
      label: "Progress Update",
      icon: "📜",
      gradient: "from-teal-500 to-teal-600",
      hoverGradient: "from-teal-600 to-teal-700",
      description: "Shows the history",
    },
  ];


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600">
            Loading scheme details...
          </p>
        </div>
      </div>
    );
  }

  if (!scheme) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4 text-gray-400">❌</div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">
            Scheme not found
          </h3>
          <p className="text-gray-600 mb-6">
            The requested scheme could not be found.
          </p>
          <button
            onClick={() => navigate("/schemes")}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
          >
            Back to Schemes
          </button>
        </div>
      </div>
    );
  }

  const progressPercentage = Math.max(
    0,
    Math.min(100, scheme.progressPercentage || 0)
  );

  const isRevenueExpected = (() => {
    if (!scheme) return false;
    const rg = scheme.revenueGeneration;
    if (rg === true || rg === 1) return true;
    if (typeof rg === "string") {
      const lower = rg.trim().toLowerCase();
      return lower === "yes" || lower === "true" || lower === "1";
    }
    return false;
  })();

  const totalRevenue = revenues.reduce(
    (acc, curr) => acc + (parseFloat(curr.revenue) || 0),
    0
  );
  const displayTotalRevenue =
    scheme?.totalRevenueGenerated != null
      ? scheme.totalRevenueGenerated
      : totalRevenue;

  return (
    <div className="min-h-screen -mt-4 -mx-4">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <IconButton onClick={() => navigate(-1)}>
                <BsArrowLeft className="text-gray-700" />
              </IconButton>
              <div className="h-6 w-px bg-gray-300"></div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {scheme.schemeName}
                </h1>
                <p className="text-gray-600 mt-1 text-sm">
                  {scheme.district} • {scheme.block} • {scheme.panchayat}
                </p>
              </div>
            </div>

            <div className="flex space-x-2 justify-end items-center flex-wrap gap-y-2">
              {isRevenueExpected ? (
                <button
                  onClick={() => setIsRevenueModalOpen(true)}
                  className="bg-emerald-600 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-emerald-700 transition-colors shadow-sm flex items-center gap-1.5"
                >
                  <span>+</span> Add Revenue
                </button>
              ) : (
                <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-semibold bg-amber-600 text-white shadow-sm">
                  Revenue Not Expected
                </span>
              )}

              <button
                onClick={() => setIsProgressModalOpen(true)}
                className="bg-blue-600 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-blue-700 transition-colors shadow-sm"
              >
                Progress Update
              </button>

              <span
                className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-semibold ${getStatusColor(
                  scheme.schemeStatus
                )}`}
              >
                Scheme Status - {scheme.schemeStatus}
              </span>
              <span
                className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-semibold ${getPaymentStatusColor(
                  scheme.paymentStatus
                )}`}
              >
                Payment Status - {scheme.paymentStatus}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <MetricCard
            title="Total Allocation"
            value={formatAmount(scheme.allocatedAmount)}
            icon="💰"
            color="blue"
          />
          <MetricCard
            title="Amount Spent"
            value={formatAmount(scheme.totalSpent)}
            icon="💸"
            color="green"
          />
          <MetricCard
            title="Amount Remaining"
            value={formatAmount(scheme.amountRemaining)}
            icon="📊"
            color="blue"
          />
          <MetricCard
            title="Total Revenue"
            value={
              isRevenueExpected
                ? formatAmount(displayTotalRevenue)
                : "Not Expected"
            }
            icon="💵"
            color={isRevenueExpected ? "emerald" : "orange"}
          />
        </div>

        {/* Progress Bar */}
        {/* <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
          <div className="flex justify-between items-center mb-3">
            <span className="text-sm font-semibold text-gray-800">
              Project Progress
            </span>
            <span className="text-sm font-bold text-gray-900">
              {progressPercentage.toFixed(1)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progressPercentage}%` }}
              transition={{ duration: 1, ease: "easeOut" }}
              className={`h-2 rounded-full ${getProgressColor(
                progressPercentage
              )}`}
            />
          </div>
        </div> */}

        {/* Enhanced Cards Navigation */}
        <div className="flex flex-row gap-3 mb-8 overflow-x-auto pb-2">
          {tabs.map((tab) => (
            <motion.button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.95 }}
              className={`bg-gradient-to-br ${tab.gradient
                } rounded-lg p-3 text-white shadow-md hover:shadow-lg transition-all duration-200 text-left border-2 min-w-[180px] flex items-center gap-3 ${activeTab === tab.id
                  ? "border-white ring-1 ring-white ring-opacity-50"
                  : "border-transparent"
                }`}
            >
              <div className="w-10 h-10 rounded-md bg-white bg-opacity-20 flex items-center justify-center flex-shrink-0">
                <span className="text-lg">{tab.icon}</span>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-sm text-white truncate">
                  {tab.label}
                </h3>
                <p className="text-white font-normal text-opacity-80 text-xs truncate">
                  {tab.description}
                </p>
              </div>
            </motion.button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === "overview" && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-6"
              >
                <SectionCard title="Basic Information">
                  <InfoField
                    label="Financial Year"
                    value={scheme.financialYear}
                  />
                  <InfoField label="Sector" value={scheme.sector} />
                  <InfoField label="Sub Sector" value={scheme.subSector} />
                  <InfoField
                    label="Revenue Generation"
                    value={isRevenueExpected ? "Yes" : "No"}
                  />
                </SectionCard>

                <SectionCard title="Location Details">
                  <InfoField label="District" value={scheme.district} />
                  <InfoField label="Block" value={scheme.block} />
                  <InfoField
                    label="Gram Panchayat"
                    value={scheme.gramPanchayat || scheme.panchayat}
                  />
                  <InfoField label="Land Address" value={scheme.landAddress} />
                  {scheme.coordinates && (
                    <InfoField
                      label="Coordinates"
                      value={`${scheme.coordinates.latitude}, ${scheme.coordinates.longitude}`}
                    />
                  )}
                </SectionCard>

                <SectionCard title="Project Details">
                  <InfoField
                    label="Utility Purpose"
                    value={scheme.utilityPurpose}
                  />
                  <InfoField
                    label="Impact Description"
                    value={scheme.impactDescription}
                  />
                  <InfoField label="Land Nature" value={scheme.landNature} />
                  <InfoField
                    label="Land Suitability"
                    value={scheme.landSuitability}
                  />
                </SectionCard>

                <SectionCard title="Officials">
                  <InfoField label="DDC-cum-CEO" value={scheme.ddcCumCEOName} />
                  <InfoField
                    label="District Engineer"
                    value={scheme.districtEngineerName}
                  />
                  <InfoField label="DDO" value={scheme.ddo} />
                  <InfoField label="DDO Code" value={scheme.ddoCode} />
                </SectionCard>
              </motion.div>
            )}

            {/* Financial Tab */}
            {activeTab === "financial" && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-6"
              >
                <SectionCard title="Financial Details">
                  <InfoField
                    label="Total Allocated"
                    value={formatAmount(scheme.totalAllocated)}
                  />
                  <InfoField
                    label="Allocated Amount"
                    value={formatAmount(scheme.allocatedAmount)}
                  />
                  <InfoField
                    label="Allocation Letter No"
                    value={scheme.allocationLetterNo}
                  />
                  <InfoField
                    label="Technical Sanction Amount"
                    value={formatAmount(scheme.technicalSanctionAmount)}
                  />
                  <InfoField
                    label="Administrative Approval Amount"
                    value={formatAmount(scheme.administrativeApprovalAmount)}
                  />
                  <InfoField
                    label="Amount Remaining"
                    value={formatAmount(scheme.amountRemaining)}
                  />
                  <InfoField
                    label="UC Amount"
                    value={formatAmount(scheme.ucAmount)}
                  />
                </SectionCard>

                <SectionCard title="Revenue Summary">
                  <InfoField
                    label="Revenue Status"
                    value={isRevenueExpected ? "Expected" : "Not Expected"}
                  />
                  <InfoField
                    label="Total Revenue Generated"
                    value={
                      isRevenueExpected
                        ? formatAmount(displayTotalRevenue)
                        : "Revenue Not Expected"
                    }
                  />
                  {isRevenueExpected && (
                    <>
                      <InfoField
                        label="Revenue Entries Count"
                        value={`${revenues.length} ${revenues.length === 1 ? "record" : "records"}`}
                      />
                      <InfoField
                        label="Latest Financial Year"
                        value={
                          revenues.length > 0
                            ? revenues[0].financialYear
                            : scheme.financialYear || "N/A"
                        }
                      />
                    </>
                  )}
                  <div className="pt-4 flex flex-col sm:flex-row gap-2">
                    {isRevenueExpected && (
                      <button
                        onClick={() => setIsRevenueModalOpen(true)}
                        className="flex-1 bg-emerald-600 text-white rounded-lg px-4 py-2.5 text-sm font-semibold hover:bg-emerald-700 transition-colors flex items-center justify-center gap-1.5 shadow-sm"
                      >
                        <span>+</span> Add Revenue
                      </button>
                    )}
                    <button
                      onClick={() => setActiveTab("revenue")}
                      className="px-4 py-2.5 border border-emerald-600 text-emerald-700 rounded-lg text-sm font-semibold hover:bg-emerald-50 transition-colors flex items-center justify-center flex-1"
                    >
                      View Revenue Records &rarr;
                    </button>
                  </div>
                </SectionCard>
              </motion.div>
            )}

            {/* Revenue Tab */}
            {activeTab === "revenue" && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-gray-100">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      Revenue Collections
                    </h3>
                    <p className="text-sm text-gray-500 mt-0.5">
                      Track and manage all revenues generated under this scheme
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="hidden sm:inline-block px-3 py-1 bg-emerald-50 text-emerald-700 rounded-lg text-sm font-bold border border-emerald-200">
                      Total: {isRevenueExpected ? formatAmount(displayTotalRevenue) : "Not Expected"}
                    </span>
                    {isRevenueExpected ? (
                      <button
                        onClick={() => setIsRevenueModalOpen(true)}
                        className="bg-emerald-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-emerald-700 transition-colors shadow-sm flex items-center gap-1.5 text-sm"
                      >
                        <span>+</span> Add Revenue
                      </button>
                    ) : (
                      <span className="inline-flex items-center px-3 py-1.5 bg-amber-100 text-amber-800 border border-amber-200 rounded-lg text-sm font-semibold">
                        Revenue Not Expected
                      </span>
                    )}
                  </div>
                </div>

                {revenueLoading ? (
                  <div className="py-16 text-center">
                    <div className="animate-spin rounded-full h-9 w-9 border-b-2 border-emerald-600 mx-auto"></div>
                    <p className="mt-3 text-sm text-gray-500">Loading revenue records...</p>
                  </div>
                ) : revenues.length > 0 ? (
                  <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
                    <table className="w-full text-left text-sm text-gray-700">
                      <thead className="bg-gray-50 border-b border-gray-200 text-xs uppercase font-semibold text-gray-600">
                        <tr>
                          <th className="px-5 py-3.5">#</th>
                          <th className="px-5 py-3.5">Financial Year</th>
                          <th className="px-5 py-3.5">Revenue Amount</th>
                          <th className="px-5 py-3.5">Remarks</th>
                          <th className="px-5 py-3.5">Recorded On</th>
                          <th className="px-5 py-3.5 text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white">
                        {revenues.map((item, index) => (
                          <tr
                            key={item.id || index}
                            className="hover:bg-emerald-50/40 transition-colors"
                          >
                            <td className="px-5 py-4 font-medium text-gray-500">
                              {index + 1}
                            </td>
                            <td className="px-5 py-4">
                              <span className="inline-block px-2.5 py-1 bg-gray-100 text-gray-800 rounded-md font-semibold text-xs border border-gray-200">
                                {item.financialYear || "N/A"}
                              </span>
                            </td>
                            <td className="px-5 py-4 font-bold text-emerald-700 text-base">
                              {formatAmount(item.revenue)}
                            </td>
                            <td className="px-5 py-4 text-gray-600 max-w-xs">
                              {item.remarks || (
                                <span className="text-gray-400 italic">No remarks</span>
                              )}
                            </td>
                            <td className="px-5 py-4 text-gray-500 whitespace-nowrap">
                              {formatDate(item.createdAt)}
                            </td>
                            <td className="px-5 py-4 text-center">
                              <button
                                onClick={() => handleDeleteRevenue(item.id)}
                                title="Delete Revenue Entry"
                                className="text-gray-400 hover:text-red-600 p-1.5 rounded-lg hover:bg-red-50 transition-colors inline-flex items-center"
                              >
                                <FiTrash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-emerald-50/60 font-semibold border-t-2 border-emerald-200">
                        <tr>
                          <td colSpan="2" className="px-5 py-3.5 text-gray-900 font-bold">
                            Total Revenue Generated
                          </td>
                          <td className="px-5 py-3.5 text-emerald-800 font-extrabold text-base">
                            {formatAmount(displayTotalRevenue)}
                          </td>
                          <td colSpan="3" className="px-5 py-3.5 text-right text-xs text-gray-500">
                            {revenues.length} {revenues.length === 1 ? "record" : "records"} recorded
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <div className="py-16 text-center bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
                    <div className="text-4xl mb-3">💵</div>
                    <h4 className="text-base font-bold text-gray-800 mb-1">
                      {isRevenueExpected
                        ? "No revenue records found"
                        : "Revenue Not Expected"}
                    </h4>
                    <p className="text-sm text-gray-500 max-w-md mx-auto mb-4">
                      {isRevenueExpected
                        ? "No revenue has been recorded for this scheme yet. Click below to add the first revenue entry."
                        : "Revenue generation is not expected for this scheme (Revenue Generation is marked as No or Not Set)."}
                    </p>
                    {isRevenueExpected && (
                      <button
                        onClick={() => setIsRevenueModalOpen(true)}
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition-colors shadow-sm text-sm"
                      >
                        <span>+</span> Add Revenue
                      </button>
                    )}
                  </div>
                )}
              </motion.div>
            )}

            {/* Approvals Tab */}
            {/* {activeTab === "approvals" && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-6"
              >
                <SectionCard title="Approval Details">
                  <InfoField
                    label="Need Assessment"
                    value={scheme.needAssessment}
                  />
                  <InfoField
                    label="Feasibility Study"
                    value={scheme.feasibilityStudy}
                  />
                  <InfoField label="DPR" value={scheme.dpr} />
                  <InfoField
                    label="Revenue Generation"
                    value={scheme.revenueGeneration ? "Yes" : "No"}
                  />
                  <InfoField
                    label="Board Approval"
                    value={scheme.boardApproval ? "Yes" : "No"}
                  />
                </SectionCard>

                <SectionCard title="Sanction Details">
                  <InfoField
                    label="Technical Sanction Authority"
                    value={scheme.technicalSanctionAuthority}
                  />
                  <InfoField
                    label="Administrative Approval Authority"
                    value={scheme.administrativeApprovalAuthority}
                  />
                  <InfoField
                    label="Admin Approval Letter No"
                    value={scheme.administrativeApprovalLetterNo}
                  />
                  <InfoField
                    label="Admin Approval Date"
                    value={formatDate(scheme.administrativeApprovalDate)}
                  />
                </SectionCard>
              </motion.div>
            )} */}

            {/* Execution Tab */}
            {activeTab === "execution" && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-6"
              >
                <SectionCard title="Execution Timeline">
                  <InfoField
                    label="Scheme Start Date"
                    value={formatDate(scheme.schemeStartDate)}
                  />
                  <InfoField
                    label="Completion Date"
                    value={formatDate(scheme.completionDate)}
                  />
                  <InfoField
                    label="Work Order Date"
                    value={formatDate(scheme.workOrderDate)}
                  />
                  <InfoField
                    label="Tender Status"
                    value={scheme.tenderStatus}
                  />
                  <InfoField label="Agency Name" value={scheme.agencyName} />
                </SectionCard>

                <SectionCard title="Monitoring">

                  <InfoField
                    label="UC Sent Letter No"
                    value={scheme.ucSentLetterNo}
                  />
                  <InfoField
                    label="UC Sent Date"
                    value={formatDate(scheme.ucSentDate)}
                  />
                  <InfoField
                    label="Last Field Visit Date"
                    value={formatDate(scheme.lastFieldVisitDate)}
                  />
                  <InfoField
                    label="Field Visit Officials"
                    value={scheme.fieldVisitOfficials}
                  />
                </SectionCard>
              </motion.div>
            )}

            {/* Documents Tab */}
            {activeTab === "documents" && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
              >
                <SectionCard title="Uploaded Documents">
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    {scheme.geoReportDoc && (
                      <a href={scheme.geoReportDoc} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-blue-600 hover:text-blue-800 bg-blue-50 p-3 rounded-lg border border-blue-100 transition-colors">
                        <span className="text-xl">📑</span>
                        <span className="font-medium text-sm">Geo Report</span>
                      </a>
                    )}
                    {scheme.feasibilityStudyFile && (
                      <a href={scheme.feasibilityStudyFile} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-blue-600 hover:text-blue-800 bg-blue-50 p-3 rounded-lg border border-blue-100 transition-colors">
                        <span className="text-xl">📑</span>
                        <span className="font-medium text-sm">Feasibility Study</span>
                      </a>
                    )}
                    {scheme.dprFile && (
                      <a href={scheme.dprFile} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-blue-600 hover:text-blue-800 bg-blue-50 p-3 rounded-lg border border-blue-100 transition-colors">
                        <span className="text-xl">📑</span>
                        <span className="font-medium text-sm">DPR File</span>
                      </a>
                    )}
                    {scheme.administrativeApprovalFile && (
                      <a href={scheme.administrativeApprovalFile} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-blue-600 hover:text-blue-800 bg-blue-50 p-3 rounded-lg border border-blue-100 transition-colors">
                        <span className="text-xl">📑</span>
                        <span className="font-medium text-sm">Admin Approval File</span>
                      </a>
                    )}

                    {scheme.tenderNoticeAttachment && typeof scheme.tenderNoticeAttachment === "string" && scheme.tenderNoticeAttachment.startsWith("http") && (
                      <a href={scheme.tenderNoticeAttachment} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-blue-600 hover:text-blue-800 bg-blue-50 p-3 rounded-lg border border-blue-100 transition-colors">
                        <span className="text-xl">📑</span>
                        <span className="font-medium text-sm">Tender Notice Attachment</span>
                      </a>
                    )}
                    {!scheme.geoReportDoc && !scheme.feasibilityStudyFile && !scheme.dprFile && !scheme.administrativeApprovalFile && (!scheme.tenderNoticeAttachment || typeof scheme.tenderNoticeAttachment !== "string" || !scheme.tenderNoticeAttachment.startsWith("http")) && (
                      <p className="text-gray-500 text-sm py-2">No documents available</p>
                    )}
                  </div>
                </SectionCard>

                <SectionCard title="Field Visit Notes">
                  {scheme.fieldVisitNotes &&
                    scheme.fieldVisitNotes.length > 0 ? (
                    <div className="space-y-3">
                      {scheme.fieldVisitNotes.map((note, index) => (
                        <div
                          key={index}
                          className="bg-gray-50 p-3 rounded border border-gray-200"
                        >
                          <p className="text-gray-700 text-sm">{note}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm py-2">
                      No field visit notes available
                    </p>
                  )}
                </SectionCard>

                <SectionCard title="Remarks">
                  <div className="bg-gray-50 p-4 rounded border border-gray-200">
                    <p className="text-gray-700">
                      {scheme.remarks || "No remarks available"}
                    </p>
                  </div>
                </SectionCard>

                <SectionCard title="CO Report">
                  <div className="bg-gray-50 p-4 rounded border border-gray-200">
                    <p className="text-gray-700">
                      {scheme.coReport || "No CO report available"}
                    </p>
                  </div>
                </SectionCard>
              </motion.div>
            )}

            {/* History Tab */}
            {activeTab === "history" && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
              >
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-bold text-gray-900">Progress History</h3>
                  <button
                    onClick={() => setIsProgressModalOpen(true)}
                    className="text-sm bg-blue-50 text-blue-600 px-3 py-1.5 rounded-lg font-semibold hover:bg-blue-100 transition-colors"
                  >
                    + Add New Update
                  </button>
                </div>

                {updatesLoading ? (
                  <div className="py-12 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2 text-gray-500">Loading history...</p>
                  </div>
                ) : progressUpdates.length > 0 ? (
                  <div className="relative border-l-2 border-blue-100 ml-4 pl-8 space-y-8">
                    {progressUpdates.map((update, index) => (
                      <div key={update.id || index} className="relative">
                        {/* Timeline dot */}
                        <div className="absolute -left-[41px] top-0 w-4 h-4 rounded-full bg-blue-500 border-4 border-white shadow-sm"></div>

                        <div className="bg-gray-50 rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <span className="text-xs font-bold text-blue-600 uppercase tracking-wider bg-blue-50 px-2 py-1 rounded">
                                {formatDate(update.updateDate)}
                              </span>
                              <h4 className="mt-2 text-lg font-bold text-gray-900">
                                {update.progressPercentage}% Completion reached
                              </h4>
                            </div>
                            <div className="w-16 h-16 rounded-full border-4 border-white shadow-inner bg-white flex items-center justify-center">
                              <span className="text-sm font-black text-blue-700">
                                {update.progressPercentage}%
                              </span>
                            </div>
                          </div>

                          <p className="text-gray-700 text-sm leading-relaxed mb-4">
                            {update.progressNotes}
                          </p>

                          {update.documentUrls && update.documentUrls.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-200">
                              {update.documentUrls.map((url, i) => (
                                <a
                                  key={i}
                                  href={url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 bg-white border border-blue-100 px-3 py-1.5 rounded-full hover:bg-blue-50 transition-colors"
                                >
                                  <span>📄</span> Doc {i + 1}
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-12 text-center bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
                    <div className="text-4xl mb-3">📅</div>
                    <p className="text-gray-500 font-medium">No progress updates yet</p>
                    <button
                      onClick={() => setIsProgressModalOpen(true)}
                      className="mt-4 text-blue-600 font-bold hover:underline"
                    >
                      Click here to add the first update
                    </button>
                  </div>
                )}
              </motion.div>
            )}
          </div>
        </div>
        <ProgressUpdateModal
          isOpen={isProgressModalOpen}
          onClose={() => setIsProgressModalOpen(false)}
          schemeId={schemeId || scheme?.id}
          onSuccess={() => {
            fetchSchemeDetails();
            fetchProgressUpdates();
          }}
        />

        <AddRevenueModal
          isOpen={isRevenueModalOpen}
          onClose={() => setIsRevenueModalOpen(false)}
          schemeId={schemeId || scheme?.id}
          schemeName={scheme?.schemeName}
          defaultFinancialYear={scheme?.financialYear}
          onSuccess={() => {
            fetchSchemeDetails();
            fetchRevenues();
          }}
        />

        {/* <div className="mt-12 flex items-center justify-end">
          <button className="bg-blue-700 text-white">Accept Request</button>
        </div> */}
      </div>
    </div>
  );
}

// Enhanced Section Card Component
const SectionCard = ({ title, children }) => (
  <div className="bg-white rounded-lg border border-gray-200 p-6 hover:border-gray-300 transition-colors">
    <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-3 border-b border-gray-200">
      {title}
    </h3>
    <div className="space-y-3">{children}</div>
  </div>
);

// Metric Card Component
const MetricCard = ({ title, value, icon, color }) => {
  const colorClasses = {
    blue: "bg-blue-500",
    green: "bg-green-500",
    orange: "bg-orange-500",
    emerald: "bg-emerald-600",
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5 hover:border-gray-300 transition-colors">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <p className="text-xl font-bold text-gray-900">{value}</p>
        </div>
        <div className={`p-3 rounded-lg ${colorClasses[color]} text-white`}>
          <span className="text-lg">{icon}</span>
        </div>
      </div>
    </div>
  );
};

// Info Field Component
const InfoField = ({ label, value }) => (
  <div className="flex justify-between items-start py-2 border-b border-gray-100 last:border-b-0">
    <span className="text-sm font-medium text-gray-600 flex-shrink-0 pr-4">
      {label}
    </span>
    <span className="text-sm text-gray-900 font-semibold text-right flex-1">
      {value || "Not available"}
    </span>
  </div>
);
